import type { ChangeEvent, ReactNode } from 'react'
import type { FieldValues } from 'react-hook-form'

import {
  type InputFieldProps,
  type UseInputFieldProps,
  useInputField,
} from '../useInputField'

import {
  StyledFieldWrapper,
  StyledInput,
  StyledInputWrapper,
  StyledLabel,
} from './TextInputField.Styled'

type Props<TFieldValues extends FieldValues> = {
  icon?: ReactNode
  label: string
  type?: 'text' | 'email' | 'password'
} & Omit<UseInputFieldProps<TFieldValues>, 'propsOnBlur'> &
  Omit<InputFieldProps, 'name' | 'type'>

export const TextInputField = <TFieldValues extends FieldValues>({
  autoFocus,
  control,
  icon,
  isDisabled,
  label,
  name,
  onBlur: propsOnBlur,
  onChange: propsOnChange,
  type = 'text',
  ...htmlProps
}: Props<TFieldValues>) => {
  const {
    error,
    handleBlur,
    handleClick,
    handleFocus,
    inputRef,
    isFocused,
    onChange,
    value,
  } = useInputField({ autoFocus, control, isDisabled, name, propsOnBlur })

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event)
    propsOnChange?.(event)
  }

  const id = `input-${name}`

  return (
    <StyledFieldWrapper
      $hasError={Boolean(error)}
      $hasValue={Boolean(value)}
      $isDisabled={Boolean(isDisabled)}
      $isFocused={isFocused}
      onClick={handleClick}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {icon}
      <StyledInputWrapper>
        <StyledInput
          {...htmlProps}
          data-testid={id}
          disabled={isDisabled}
          id={id}
          name={name}
          onChange={handleChange}
          ref={inputRef}
          type={type}
          value={value}
        />
        <StyledLabel $isDisabled={Boolean(isDisabled)} htmlFor={id}>
          {label}
        </StyledLabel>
      </StyledInputWrapper>
    </StyledFieldWrapper>
  )
}
